import { Card } from '../atoms'
import { useMemo } from 'react'
import type { Position } from '../../types'
import { haversineMeters, now } from '../../utils/geo'

export interface RouteStatsProps {
  positions: Array<Position & { accuracy?: number }>
  startTime: number | null
}

export const RouteStats = ({ positions, startTime }: RouteStatsProps) => {
  const stats = useMemo(() => {
    if (positions.length < 2) {
      return {
        distance: 0,
        duration: 0,
        avgSpeed: 0,
        maxSpeed: 0,
      }
    }

    let totalDistance = 0
    const speeds: number[] = []

    for (let i = 1; i < positions.length; i++) {
      const prev = positions[i - 1]
      const curr = positions[i]
      const dist = haversineMeters(prev.lat, prev.lon, curr.lat, curr.lon)
      totalDistance += dist

      const timeDiff = (curr.t - prev.t) / 1000 // seconds
      if (timeDiff > 0) {
        const speed = (dist / timeDiff) * 3.6 // km/h
        speeds.push(speed)
      }
    }

    const duration = startTime ? (now() - startTime) / 1000 : 0
    const avgSpeed =
      speeds.length > 0
        ? speeds.reduce((a, b) => a + b, 0) / speeds.length
        : 0
    const maxSpeed = speeds.length > 0 ? Math.max(...speeds) : 0

    return {
      distance: totalDistance,
      duration,
      avgSpeed,
      maxSpeed,
    }
  }, [positions, startTime])

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`
    }
    return `${secs}s`
  }

  return (
    <Card>
      <h3 className="font-semibold mb-3">Route Statistics</h3>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-text-muted">Distance</div>
          <div className="font-medium">
            {stats.distance >= 1000
              ? `${(stats.distance / 1000).toFixed(2)} km`
              : `${Math.round(stats.distance)} m`}
          </div>
        </div>
        <div>
          <div className="text-text-muted">Duration</div>
          <div className="font-medium">{formatDuration(stats.duration)}</div>
        </div>
        <div>
          <div className="text-text-muted">Avg Speed</div>
          <div className="font-medium">
            {stats.avgSpeed > 0 ? `${stats.avgSpeed.toFixed(1)} km/h` : '-'}
          </div>
        </div>
        <div>
          <div className="text-text-muted">Max Speed</div>
          <div className="font-medium">
            {stats.maxSpeed > 0 ? `${stats.maxSpeed.toFixed(1)} km/h` : '-'}
          </div>
        </div>
      </div>
    </Card>
  )
}
